import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Title, LaneHeader } from '../common/board/styles/Base';
import InlineInputController from '../common/board/widgets/InlineInput';
import ProgrammePlanDayActionsPopupMenu from './programme_plan_day_actions_popup_menu';
import ProgrammePlanNutritionHeaderCard from './programme_plan_nutrition_header_card';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useCreateContentEntryMutation,
  useGetProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetContentEntryQuery,
  useLazyGetProgrammeQuery,
  useUpdateProgrammeMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  copyProgrammePlanDayToProgramme,
  removeMealsAndGRCRecipesFromProgrammePlanDay,
} from '../../operations/programmes_operations';
import { healthProGroupForSpaceID } from '../../services/users';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { ActivityObjectType } from '../../API';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const ProgrammePlanDayHeader = ({
  id,
  appBoardId: programmePlanId,
  onDoubleClick,
  editLaneTitle,
  title,
  titleStyle,
  laneDraggable,
  updateTitle,
}) => {
  const dispatch = useDispatch();
  const setGlobalSnackbar = (obj) => dispatch(showGlobalSnackbar(obj));

  const isPreview = !!titleStyle._isPreview;

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery();

  const programme = (ownedProgrammesUnsorted || []).find((pr) =>
    pr.plans.some((pl) => pl.id === programmePlanId),
  );

  const plan = (programme?.plans || []).find((pl) => pl.id === programmePlanId);

  const day = (plan?.days || []).find((d) => d.id === id);

  const { data: healthProSpace } = useGetSpaceQuery();

  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const [lazyGetProgramme] = useLazyGetProgrammeQuery();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const [lazyGetContentEntry] = useLazyGetContentEntryQuery();

  const [createContentEntry] = useCreateContentEntryMutation();

  // console.log(`MenuHeader recipesBoardId ${recipesBoardId}`);

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  return (
    <>
      <LaneHeader onDoubleClick={onDoubleClick} editLaneTitle={editLaneTitle}>
        <div>
          <Title draggable={laneDraggable}>
            {editLaneTitle ? (
              <InlineInputController
                value={title}
                border
                placeholder="My placeholder"
                resize="vertical"
                onSave={updateTitle}
              />
            ) : (
              title
            )}
          </Title>
        </div>
        {!isPreview && (
          <ProgrammePlanDayActionsPopupMenu
            programmeId={programme.id}
            onCopyToProgramme={async (
              toProgrammeId,
              toProgrammePlanId,
              dayIndexes,
            ) => {
              reportAccountActivitySignalOperation({
                spaceID: healthProSpace.id,
                friendlyUsername,
                lastActivityObjectType: ActivityObjectType.PROGRAMMES,
                lastActivityObjectID: programme.id,
              });
              copyProgrammePlanDayToProgramme(
                day,
                toProgrammeId,
                toProgrammePlanId,
                dayIndexes,
                currentHealthProGroup,
                lazyGetProgramme,
                updateProgramme,
                lazyGetContentEntry,
                createContentEntry,
              );
            }}
            onRemoveMealsAndGRCRecipes={async () => {
              reportAccountActivitySignalOperation({
                spaceID: healthProSpace.id,
                friendlyUsername,
                lastActivityObjectType: ActivityObjectType.PROGRAMMES,
                lastActivityObjectID: programme.id,
              });
              return removeMealsAndGRCRecipesFromProgrammePlanDay(
                programme,
                programmePlanId,
                id,
                updateProgramme,
              );
            }}
            setGlobalSnackbar={setGlobalSnackbar}
          />
        )}
        {isPreview && (
          <div style={{ height: '34px' }}>&nbsp; {/* No icon */}</div>
        )}
      </LaneHeader>
      {titleStyle?._nutritionHeader &&
        titleStyle?._nutritionHeader?.showNutritionToUsers && (
          <ProgrammePlanNutritionHeaderCard
            width={252}
            targetNutrition={titleStyle._nutritionHeader.targetNutrition}
            actualTotalNutrition={
              titleStyle._nutritionHeader.actualTotalNutrition
            }
            preferredMetricsWithRules={
              titleStyle._nutritionHeader.preferredMetricsWithRules
            }
            actualMacroRatio={titleStyle._nutritionHeader.actualMacroRatio}
          />
        )}
    </>
  );
};

ProgrammePlanDayHeader.propTypes = {
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  editLaneTitle: PropTypes.bool.isRequired,
  laneDraggable: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  titleStyle: PropTypes.object.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  updateTitle: PropTypes.func.isRequired,
};

export default ProgrammePlanDayHeader;
